import {
  createAsync,
  Navigate,
  Route,
  Router,
  useLocation,
  useNavigate,
  useParams,
} from '@solidjs/router';
import { file } from 'opfs-tools';
import {
  createEffect,
  createSignal,
  type JSX,
  lazy,
  Show,
  Suspense,
} from 'solid-js';
import { useI18n } from './lib/i18n';
import { isAuthenticated } from './lib/supabase';
import { type IProjectConfig } from './lib/types';

// 懒加载各个页面组件
const LazyLogin = lazy(() => import('./pages/Login'));
const LazyCallback = lazy(() => import('./pages/Callback'));
const LazyResetPassword = lazy(() => import('./pages/ResetPassword'));
const LazyProjects = lazy(() => import('./pages/Projects'));
const LazyCreateProject = lazy(
  () => import('./pages/CreateProject/CreateProject'),
);
const LazyPoints = lazy(() => import('./pages/Points'));
const LazyAdminRecharge = lazy(() => import('./pages/AdminRecharge'));
const LazyEditorPage = lazy(() => import('./pages/EditorPage/EditorPage'));
const LazyHome = lazy(() => import('./pages/Home'));

async function readPrjData(id: string) {
  const projectsFile = file(`/ZingAI/projects.json`);
  if (await projectsFile.exists()) {
    const content = (await projectsFile.text()) ?? '[]';
    const projectsData = JSON.parse(content) as IProjectConfig[];
    const project = projectsData.find((p) => p.id === id);
    return project;
  }
}

function App() {
  const { t } = useI18n();
  return (
    <>
      {/* <div class="p-4">
        <button class="p-2 border rounded mr-2" onClick={() => setLang('zh')}>
          中文
        </button>
        <button class="p-2 border rounded" onClick={() => setLang('en')}>
          English
        </button>
      </div> */}
      <Router>
        <Route path="/" component={() => <LazyHome />}></Route>
        <Route path="/login" component={() => <LazyLogin />}></Route>
        <Route path="/callback" component={() => <LazyCallback />}></Route>
        <Route
          path="/"
          component={() => <div class="p-4 text-xl">{t('app.welcome')}</div>}
        ></Route>
        <Route
          path="/login"
          component={() => (
            <Suspense fallback={t('app.loading')}>
              <LazyLogin />
            </Suspense>
          )}
        ></Route>
        <Route
          path="/callback"
          component={() => (
            <Suspense fallback={t('app.loading')}>
              <LazyCallback />
            </Suspense>
          )}
        ></Route>
        <Route
          path="/reset-password"
          component={() => (
            <Suspense fallback={t('app.loading')}>
              <LazyResetPassword />
            </Suspense>
          )}
        ></Route>
        <Route
          path="/projects"
          component={() => (
            <LoginCheck>
              <Suspense fallback={t('app.loading')}>
                <LazyProjects />
              </Suspense>
            </LoginCheck>
          )}
        ></Route>
        <Route
          path="/projects/create"
          component={() => (
            <LoginCheck>
              <Suspense fallback={t('app.loading')}>
                <LazyCreateProject />
              </Suspense>
            </LoginCheck>
          )}
        ></Route>
        <Route
          path="/points"
          component={() => (
            <LoginCheck>
              <Suspense fallback={t('app.loading')}>
                <LazyPoints />
              </Suspense>
            </LoginCheck>
          )}
        ></Route>
        <Route
          path="/admin"
          component={() => (
            <LoginCheck>
              <Suspense fallback={t('app.loading')}>
                <LazyAdminRecharge />
              </Suspense>
            </LoginCheck>
          )}
        ></Route>
        <Route
          path="/editor/:id"
          component={() => {
            const params = useParams();
            return <Navigate href={`/editor/${params.id}/caption`} />;
          }}
        ></Route>
        <Route
          path="/editor/:id/:page"
          component={() => (
            <LoginCheck>
              {() => {
                const params = useParams();
                const prjConf = createAsync(() => readPrjData(params.id));

                return (
                  <Suspense fallback={t('app.loading')}>
                    {prjConf() == null ? (
                      t('app.error404')
                    ) : (
                      <LazyEditorPage prjConf={prjConf()!} />
                    )}
                  </Suspense>
                );
              }}
            </LoginCheck>
          )}
        ></Route>
      </Router>
    </>
  );
}

function LoginCheck({
  children,
}: {
  children: JSX.Element | (() => JSX.Element);
}) {
  const { t } = useI18n();
  const [authenticated, setAuthenticated] = createSignal(false);
  const navigate = useNavigate();
  const location = useLocation();

  createEffect(async () => {
    const authStatus = await isAuthenticated();
    if (!authStatus) {
      // 如果当前不在登录页面，才进行重定向
      if (location.pathname !== '/login') {
        navigate('/login');
      }
      return;
    }
    setAuthenticated(authStatus);
  });

  return (
    <Show when={authenticated()} fallback={t('app.loading')}>
      {typeof children === 'function' ? children() : children}
    </Show>
  );
}

export default App;
